import {
	AfterViewInit,
	Component,
	ContentChild,
	DestroyRef,
	Input,
	Optional,
	Output,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild,
	EventEmitter
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort, SortDirection } from '@angular/material/sort';
import { Observable, Subject, Subscription, timer } from 'rxjs';
import { TableService } from '../../services/table.service';
import { IColumnType, ITableData } from '../../models/table';
import { Translates } from '../ph-simple-table/translates';
import { CheckboxesService } from '../../services/checkboxes.service';
import { TableVirtualScrollDataSource, TableVirtualScrollModule } from 'ng-table-virtual-scroll';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { ListsColumnsKey } from '../../services/session.service';
import { SessionService } from '../../services/session.service';
import { Utils } from '../../services/utils.service';

import { SHARED_MODULES } from '../../../shared.';
import { CheckboxStatusPipe } from '../../pipes/checkbox-status.pipe';
import { DtypeInjectorPipe } from '../../pipes/dtype-injector.pipe';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';

export interface SortingListData {
	value: string;
	direction: SortDirection;
	active: string;
}

const ITEM_SIZE_LEFT_LOAD: number = 25, // for how many elements to load the following
	LOADING_SKELETON_SHOW_DELAY: number = 400, // ms for delay showed skeleton on list loading
	LOADING_SKELETON_COLUMNS_COUNT: number = 8,
	LOADING_SKELETON_ROWS_COUNT: number = 30;

@Component({
    selector: 'app-ph-list',
    templateUrl: 'ph-list.component.html',
    styleUrls: ['ph-list.component.scss'],
	imports: [
		SHARED_MODULES,
		ScrollingModule,
		TableVirtualScrollModule,
		CheckboxStatusPipe,
		DtypeInjectorPipe,
		NgxSkeletonLoaderComponent,
		PhIconWrapperComponent
	],
    standalone: true
})
export class PhListComponent implements OnInit, AfterViewInit, OnDestroy {

	readonly expandedDetailColumns: Array<string> = ['expandedDetail'];
	columns: Array<IColumnType> = [];
	displayColumns: Array<string> = [];
	dtypes: any = null;
	isLoadedNext: boolean = false;
	isListLoading: boolean = false;
	itemSize: number = 0;
	loadingSubscription: Subscription;
	nextPage: boolean = false;
	noData: boolean = true;
	noFilterData: boolean = false;
	settingsColumns: Array<IColumnType>;
	tableData: TableVirtualScrollDataSource<ITableData<any>>;
	translates = Translates;
	visibleColumnsCount: number = 0;

	@Input() isLoading: boolean = false;
	@Input() isOptions: boolean = true;
	@Input() showCheckboxes: boolean = true;
	@Input() savedSort: SortingListData;
	@Input() draggableColumns: boolean = false;
	@Input() sortingSubject: Subject<SortingListData>;
	@Input() updateSubject: Subject<void>;
	@Input() translateColumns: Array<string> = [];
	@Input() columnsKey: ListsColumnsKey;
	@Input() enableRowExpansion: boolean = false;
	@Input() loadChildrenCallback: (id: number) => Observable<any>;
	@Input('dataSource') set dataSource(data: ITableData<any>) {
		if (!data) return;
		this.init(data);
	}

	@Output('listHeight') listHeight: EventEmitter<number> = new EventEmitter<number>();

	@ViewChild('table') table;
	@ViewChild(MatSort) sort: MatSort;
	@ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;

	@ContentChild(TemplateRef) templateRef: TemplateRef<any>;

	constructor(
		private readonly destroyRef: DestroyRef,
		private sessionService: SessionService,
		private tableService: TableService,
		@Optional() public checkboxesService: CheckboxesService
	) {}

	ngOnInit(): void {
		this.dtypes = this.tableService.dtype;
		if (this.isLoading) this.applySkeletonPlaceholder();
	}

	ngAfterViewInit(): void {
		this.sort.sortChange.subscribe(() => {
			let sorting;
			if (!this.sort.direction) sorting = 'default';
			 else if (this.sort.direction === 'asc') sorting = this.sort.active;
				else sorting = '-' + this.sort.active;

			this.sortingSubject.next({
				value: sorting,
				direction: this.sort.direction,
				active: this.sort.active
			});
		});
	}

	init(data: ITableData<any>): void {
		timer(0).subscribe(() => {
			if (this.showCheckboxes) {
				this.checkboxesService.init(data.count, data.disabled_count);
			}
			this.listHeight.emit(this.table._elementRef.nativeElement.clientHeight);
		});
		this.nextPage = !!data.next;
		if (!this.columns.length || this.isListLoading || this.columnsChanged(data.results.columns)) {
			data.results.columns.forEach((column: IColumnType) => {
				if (column.always_visible) {
					
					column.visible = true;
					return;
				}
				if (!this.columnsKey || !this.sessionService.defaults.settings[this.columnsKey]) {
					column.visible = column.default_visible;
				} else {
					column.visible = this.sessionService.defaults.settings[this.columnsKey].includes(column.key);
				}

			});
			this.columns = data.results.columns;
			this.settingsColumns = JSON.parse(JSON.stringify(this.columns.filter((column: IColumnType) => !column.button)));
			this.visibleColumnsCount = this.settingsColumns.filter((column: IColumnType) => column.visible).length;
			Utils.calculateColumnsSize(data.results.columns, this.visibleColumnsCount);
			this.displayColumns = [
				...(this.showCheckboxes ? ['select'] : []),
				...(this.enableRowExpansion ? ['expand'] : []),
				...this.columns.map(col => col.key),
				...(this.isOptions ? ['options'] : [])
			];
		} else {
			data.results.columns.forEach((column: IColumnType) => {
				const findColumn: IColumnType = this.columns.find((c: IColumnType) => c.key === column.key);
				if (findColumn) findColumn.dtype = column.dtype;
			});
		}

		this.tableData = new TableVirtualScrollDataSource(data.results.dataSource);
		this.noData = !data.has_data;
		this.noFilterData = data.has_data && !data.results.dataSource.length;
		this.isLoadedNext = false;
		this.isListLoading = false;
		if (this.loadingSubscription) this.loadingSubscription.unsubscribe();
		this.itemSize = data.results.dataSource.length;
	}

	private columnsChanged(columns: Array<IColumnType>): boolean {
		if (this.columns.length !== columns.length) return true;

		return this.columns.some((column: IColumnType, index: number) => (
			column.key !== columns[index].key || column.title !== columns[index].title
		));
	}

	private applySkeletonPlaceholder(): void {
		const columns: Array<IColumnType> = Array.from(
			{ length: LOADING_SKELETON_COLUMNS_COUNT },
			(_, index) => ({
				key: `__skeleton_${index}`,
				dtype: 'string',
				sorter: false,
				title: ' ',
				size: Math.floor(100 / LOADING_SKELETON_COLUMNS_COUNT),
				default_visible: true,
				always_visible: true,
				visible: true,
			})
		);
		const dataSource: Array<any> = Array.from(
			{ length: LOADING_SKELETON_ROWS_COUNT },
			(_, index) => ({ key: index + 1 })
		);

		this.columns = columns;
		this.settingsColumns = [];
		this.visibleColumnsCount = columns.length;
		Utils.calculateColumnsSize(columns, this.visibleColumnsCount);
		this.displayColumns = [
			...(this.showCheckboxes ? ['select'] : []),
			...(this.enableRowExpansion ? ['expand'] : []),
			...columns.map((column) => column.key),
			...(this.isOptions ? ['options'] : []),
		];
		this.tableData = new TableVirtualScrollDataSource(dataSource);
		this.noData = false;
		this.noFilterData = false;
		this.nextPage = false;
		this.isListLoading = true;
		this.itemSize = dataSource.length;
	}

	protected nestedSkeletonRowIndexes(): Array<number> {
		return Array.from({ length: Math.min(Math.max(3, 1), 5) }, (_, index: number) => index);
	}

	protected hasExpandablePayload(row: any): boolean {
		if (!this.enableRowExpansion) return false;
		return !!row.children.dataSource?.length;
	}

	protected expansionDetailRowWhen = (_index: number, row: any): boolean => !!row.expanded;

	public toggleChildren(row: any, event?: MouseEvent): void {
		if (event) event.stopPropagation();
		if (!row || !this.hasExpandablePayload(row)) return;

		if (row.expanded) {
			row.expanded = false;
			this.bumpVirtualScrollDataSlice();
			return;
		}

		row.expanded = true;

		if (this.loadChildrenCallback && !row.children.dataSource?.length) {
			row.expandChildrenLoading = true;
			this.bumpVirtualScrollDataSlice();
			this.loadChildrenCallback(row.key)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: (payload: unknown) => row.children = payload,
					error: () => row.expanded = false,
					complete: () => {
						row.expandChildrenLoading = false;
						this.bumpVirtualScrollDataSlice();
					}
				});
			return;
		}

		this.bumpVirtualScrollDataSlice();
	}

	private bumpVirtualScrollDataSlice(): void {
		if (!this.tableData.data.length) return;
		this.tableData.data = this.tableData.data.slice();
	}

	masterToggle(): void {
		this.checkboxesService.masterToggle();
	}

	scroll(itemIndex: number): void {
		if (this.isLoadedNext || !this.nextPage) return;
		if (itemIndex > this.itemSize - ITEM_SIZE_LEFT_LOAD) {
			this.isLoadedNext = true;
			this.updateSubject.next();
		}
	}

	loadingNewItems(): void {
		timer(0).subscribe(() => this.virtualScroll.scrollToOffset(0, 'auto'));
		// for future, maybe need add new list loading trigger
		if (this.loadingSubscription) this.loadingSubscription.unsubscribe();
		this.loadingSubscription = timer(LOADING_SKELETON_SHOW_DELAY).subscribe(() => {
			this.isListLoading = true
		});
	}

	setDisplayingColumns(): void {
		const displayedColumns: Array<string> = this.settingsColumns
			.filter((column: IColumnType) => column.visible)
			.map((column: IColumnType) => column.key);

		if (!Utils.equalLists(this.sessionService.defaults.settings[this.columnsKey] || [], displayedColumns)) {
			this.columns.forEach((column: IColumnType) => {
				column.visible = column.always_visible || displayedColumns.includes(column.key)
			});

			this.visibleColumnsCount = this.settingsColumns.filter((column: IColumnType) => column.visible).length;
			Utils.calculateColumnsSize(this.columns, this.visibleColumnsCount);

			if (this.columnsKey) {
				this.sessionService.defaults.settings[this.columnsKey] = displayedColumns;
				this.sessionService.updateSettings();
			}
		}
	}

	toggle(row: number, checked: boolean): void {
		this.checkboxesService.toggle(row, checked);
	}

	ngOnDestroy(): void {
		if (this.loadingSubscription) this.loadingSubscription.unsubscribe();
	}
}
